/**
 * Today's Macro Progress
 * Compact inline macro bars for dashboard view
 */

import { Flame, Beef, Wheat, Droplet } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useDailySummary, calculateProgress } from '@/hooks/use-nutrition-summary';
import { DEFAULT_GOALS, MACRO_COLORS } from '@/lib/nutrition';

interface TodaysMacroProgressProps {
  clientId: string;
  compact?: boolean;
}

interface MacroBarProps {
  label: string;
  current: number;
  target: number;
  unit: string;
  color: string;
  icon: React.ReactNode;
}

function MacroBar({ label, current, target, color, icon }: MacroBarProps) {
  const { percentage, status } = calculateProgress(current, target);
  const clampedPercentage = Math.min(percentage, 100);

  const statusTextColors = {
    under: 'text-muted-foreground',
    on_target: 'text-success',
    over: 'text-warning',
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          {icon}
          <span className="text-xs font-medium">{label}</span>
        </div>
        <span className={cn('text-xs tabular-nums', statusTextColors[status])}>
          {current.toFixed(0)} / {target}
        </span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{
            width: `${clampedPercentage}%`,
            backgroundColor: color
          }}
        />
      </div>
    </div>
  );
}

export function TodaysMacroProgress({ clientId, compact = false }: TodaysMacroProgressProps) {
  const { data: summary, isLoading } = useDailySummary({ clientId });

  const goal = summary?.goal;
  const totals = {
    calories: summary?.total_calories ?? 0,
    protein: summary?.total_protein_g ?? 0,
    carbs: summary?.total_carbs_g ?? 0,
    fat: summary?.total_fat_g ?? 0,
  };

  const targets = {
    calories: goal?.target_calories ?? DEFAULT_GOALS.calories,
    protein: goal?.target_protein_g ?? DEFAULT_GOALS.protein_g,
    carbs: goal?.target_carbs_g ?? DEFAULT_GOALS.carbs_g,
    fat: goal?.target_fat_g ?? DEFAULT_GOALS.fat_g,
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-4">
          <div className="animate-pulse space-y-3">
            <div className="h-2 bg-muted rounded w-full" />
            <div className="h-2 bg-muted rounded w-full" />
            <div className="h-2 bg-muted rounded w-full" />
            <div className="h-2 bg-muted rounded w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const calProgress = calculateProgress(totals.calories, targets.calories);

  if (compact) {
    return (
      <div className="space-y-3">
        <MacroBar
          label="Calories"
          current={totals.calories}
          target={targets.calories}
          unit="kcal"
          color="#f97316"
          icon={<Flame className="h-3.5 w-3.5 text-chart-7" />}
        />
        <div className="grid grid-cols-3 gap-2">
          <MacroBar
            label="Protein"
            current={totals.protein}
            target={targets.protein}
            unit="g"
            color={MACRO_COLORS.protein}
            icon={<Beef className="h-3 w-3 text-chart-5" />}
          />
          <MacroBar
            label="Carbs"
            current={totals.carbs}
            target={targets.carbs}
            unit="g"
            color={MACRO_COLORS.carbs}
            icon={<Wheat className="h-3 w-3 text-chart-2" />}
          />
          <MacroBar
            label="Fat"
            current={totals.fat}
            target={targets.fat}
            unit="g"
            color={MACRO_COLORS.fat}
            icon={<Droplet className="h-3 w-3 text-chart-3" />}
          />
        </div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Flame className="h-4 w-4 text-chart-7" />
          Today's Progress
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Calories - highlighted */}
        <div className="p-3 rounded-lg bg-gradient-to-r from-chart-7/10 to-chart-3/10">
          <MacroBar
            label="Calories"
            current={totals.calories}
            target={targets.calories}
            unit="kcal"
            color="#f97316"
            icon={<Flame className="h-4 w-4 text-chart-7" />}
          />
        </div>

        {/* Macros */}
        <div className="space-y-2.5">
          <MacroBar
            label="Protein"
            current={totals.protein}
            target={targets.protein}
            unit="g"
            color={MACRO_COLORS.protein}
            icon={<Beef className="h-3.5 w-3.5 text-chart-5" />}
          />
          <MacroBar
            label="Carbs"
            current={totals.carbs}
            target={targets.carbs}
            unit="g"
            color={MACRO_COLORS.carbs}
            icon={<Wheat className="h-3.5 w-3.5 text-chart-2" />}
          />
          <MacroBar
            label="Fat"
            current={totals.fat}
            target={targets.fat}
            unit="g"
            color={MACRO_COLORS.fat}
            icon={<Droplet className="h-3.5 w-3.5 text-chart-3" />}
          />
        </div>

        {/* Quick status */}
        <div className="pt-2 border-t text-center">
          <span className={cn(
            'text-xs font-medium',
            calProgress.status === 'on_target' ? 'text-success' :
            calProgress.status === 'over' ? 'text-warning' :
            'text-muted-foreground'
          )}>
            {calProgress.percentage}% of daily goal
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
